/* ============================================================================
   Costa Pickleball — Klaviyo Subscribe + A/B Scaffold
   ----------------------------------------------------------------------------
   Purpose: Route all Costa-styled email signup forms to the Klaviyo Email List
            via the public Client Subscriptions API, while keeping the DOM and
            styles fully under theme control.

   Architecture:
     - costaKlaviyoSubscribe(form, emailInput, button, source, extraProps)
       Single entry point called from any form's onsubmit. POSTs to Klaviyo
       with `costa_source` + optional per-form props. Returns false to block
       native submit.
     - A/B scaffold: Any form with data-ab-test="true" gets a deterministic
       variant assigned via cookie. Variant copy lives in the form markup on
       [data-variant="A"|"B"] blocks. applyCostaAB() swaps headline/body/CTA
       at page load. Variant is attached to every submission as a profile
       property (costa_ab_variant) so segmentation in Klaviyo is trivial.

   Klaviyo configuration (reference):
     Company (public) ID: SzzA8S
     Default target list: Email List (UjYtuQ) — double opt-in enabled
     API endpoint:        https://a.klaviyo.com/client/subscriptions/
     API revision:        2024-10-15
   ========================================================================== */

(function() {
  'use strict';

  var KLAVIYO_COMPANY_ID = 'SzzA8S';
  var KLAVIYO_LIST_ID    = 'UjYtuQ';
  var KLAVIYO_ENDPOINT   = 'https://a.klaviyo.com/client/subscriptions/';
  var KLAVIYO_REVISION   = '2024-10-15';
  var AB_COOKIE_NAME     = 'costa_ab';
  var AB_COOKIE_DAYS     = 30;

  /* --------------------------------------------------------------------------
     A/B VARIANT ASSIGNMENT
     Deterministic per-visitor via first-party cookie. 50/50 split.
     ------------------------------------------------------------------------ */
  function getOrSetABVariant() {
    var m = document.cookie.match(new RegExp('(?:^|;\\s*)' + AB_COOKIE_NAME + '=([^;]+)'));
    if (m) return m[1];
    var variant = Math.random() < 0.5 ? 'A' : 'B';
    var exp = new Date();
    exp.setTime(exp.getTime() + AB_COOKIE_DAYS * 24 * 60 * 60 * 1000);
    document.cookie = AB_COOKIE_NAME + '=' + variant +
      ';expires=' + exp.toUTCString() + ';path=/;SameSite=Lax';
    return variant;
  }

  /* --------------------------------------------------------------------------
     A/B CONTENT APPLICATION
     Forms opt in by setting data-ab-test="true" on the <form> element and
     placing the wrapper on an ancestor with data-costa-form-wrapper. Variant
     copy is stored in hidden data holders like:
       <template data-variant="A" data-headline="..." data-body="..." data-cta="..."></template>
       <template data-variant="B" data-headline="..." data-body="..." data-cta="..."></template>
     The targets are any elements inside the wrapper with data-costa-headline,
     data-costa-body, or data-costa-cta.
     ------------------------------------------------------------------------ */
  function applyCostaAB(form) {
    if (form.getAttribute('data-ab-test') !== 'true') return null;

    var wrapper = form.closest('[data-costa-form-wrapper]');
    if (!wrapper) return null;

    var variant = getOrSetABVariant();
    var holder  = wrapper.querySelector('[data-variant="' + variant + '"]');
    if (!holder) return variant;

    var headline = holder.getAttribute('data-headline');
    var body     = holder.getAttribute('data-body');
    var cta      = holder.getAttribute('data-cta');

    var headlineEl = wrapper.querySelector('[data-costa-headline]');
    var bodyEl     = wrapper.querySelector('[data-costa-body]');
    var ctaEl      = wrapper.querySelector('[data-costa-cta]') || form.querySelector('button[type="submit"]');

    if (headline && headlineEl) headlineEl.textContent = headline;
    if (body     && bodyEl)     bodyEl.textContent     = body;
    if (cta      && ctaEl)      ctaEl.textContent      = cta;

    return variant;
  }

  /* --------------------------------------------------------------------------
     MESSAGE DISPLAY
     Inserts a success/error banner immediately after the form. Success
     auto-dismisses in 8 seconds; errors persist until the next submit.
     ------------------------------------------------------------------------ */
  function showMessage(form, text, type) {
    var parent = form.parentNode;
    var existing = parent.querySelector('.costa-form-message');
    if (existing) existing.remove();

    var msg = document.createElement('div');
    msg.className = 'costa-form-message costa-form-message--' + type;
    msg.setAttribute('role', type === 'error' ? 'alert' : 'status');
    msg.textContent = text;

    var base = 'margin-top:12px;padding:10px 14px;border-radius:8px;font-size:14px;line-height:1.4;';
    if (type === 'success') {
      msg.style.cssText = base + 'background:rgba(255,255,255,0.2);color:#fff;border:1px solid rgba(255,255,255,0.3);';
    } else {
      msg.style.cssText = base + 'background:rgba(242,123,36,0.12);color:#F27B24;border:1px solid rgba(242,123,36,0.4);';
    }

    parent.insertBefore(msg, form.nextSibling);
    if (type === 'success') {
      setTimeout(function() { if (msg.parentNode) msg.remove(); }, 8000);
    }
  }

  /* --------------------------------------------------------------------------
     EMAIL VALIDATION
     Conservative pattern — rejects obvious garbage, accepts anything that
     resembles a normal address. Klaviyo does stricter validation server-side.
     ------------------------------------------------------------------------ */
  function isValidEmail(email) {
    return !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /* --------------------------------------------------------------------------
     MAIN SUBSCRIBE FUNCTION
     Public entry point. Call from any form's onsubmit:
       onsubmit="return costaKlaviyoSubscribe(this, this.querySelector('input[type=email]'), this.querySelector('button'), 'homepage_newsletter');"
     ------------------------------------------------------------------------ */
  window.costaKlaviyoSubscribe = function(form, emailInput, button, source, extraProps) {
    var email = (emailInput && emailInput.value || '').trim();

    if (!isValidEmail(email)) {
      showMessage(form, 'Please enter a valid email address.', 'error');
      if (emailInput && emailInput.focus) emailInput.focus();
      return false;
    }

    var originalCtaText = button ? button.textContent : '';
    if (button) {
      button.disabled = true;
      button.textContent = 'Subscribing…';
    }

    var properties = { costa_source: source || 'costa_website' };
    if (form.getAttribute('data-ab-test') === 'true') {
      properties.costa_ab_variant = getOrSetABVariant();
    }
    if (extraProps && typeof extraProps === 'object') {
      for (var k in extraProps) {
        if (Object.prototype.hasOwnProperty.call(extraProps, k)) {
          properties[k] = extraProps[k];
        }
      }
    }

    /* Klaviyo Client Subscriptions endpoint (revision 2024-10-15).
       Per Klaviyo docs: when `subscriptions` is omitted, the endpoint defaults to
       MARKETING — hitting this endpoint IS the subscribe instruction. Including an
       explicit subscriptions block causes a 400 ("not a valid field") under this
       revision. The list's double_opt_in setting (on UjYtuQ) governs whether the
       confirmation email fires.
       The Content-Type MUST be `application/vnd.api+json` (JSON:API). Plain
       `application/json` is rejected / silently no-ops the subscribe action. */
    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source || 'Costa Website',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: properties
              }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: KLAVIYO_LIST_ID } }
        }
      }
    };

    fetch(KLAVIYO_ENDPOINT + '?company_id=' + KLAVIYO_COMPANY_ID, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        'revision': KLAVIYO_REVISION
      },
      body: JSON.stringify(payload)
    })
    .then(function(response) {
      /* Klaviyo returns 202 Accepted on success. Anything else is an error. */
      if (response.status === 202 || response.ok) {
        showMessage(form, 'Thanks! Check your email to confirm your subscription.', 'success');
        if (button) {
          button.textContent = 'Subscribed ✓';
          /* Mark as subscribed so CSS can neutralize the `cursor: wait` that
             applies while the button is :disabled during the submit fetch.
             Keeping button.disabled = true prevents double-submit; the class
             just signals "this is a success state, not an in-flight state." */
          button.classList.add('is-subscribed');
        }
        if (emailInput) emailInput.value = '';
        /* If klaviyo.js is loaded, also identify the profile client-side for onsite tracking. */
        if (window._learnq && typeof window._learnq.push === 'function') {
          window._learnq.push(['identify', { '$email': email }]);
        }
        /* Dispatch a DOM event so quiz flows or GA listeners can react. */
        form.dispatchEvent(new CustomEvent('costa:subscribed', {
          bubbles: true,
          detail: { email: email, source: source, properties: properties }
        }));
        return;
      }
      return response.json().then(function(data) {
        var detail = data && data.errors && data.errors[0] && data.errors[0].detail;
        throw new Error(detail || 'Subscription failed. Please try again.');
      });
    })
    .catch(function(error) {
      console.error('[Costa Klaviyo]', error);
      showMessage(form, 'Something went wrong. Please try again in a moment.', 'error');
      if (button) {
        button.disabled = false;
        button.textContent = originalCtaText;
      }
    });

    return false;
  };

  /* --------------------------------------------------------------------------
     APPLY A/B VARIANTS AT PAGE LOAD
     Runs once after DOM is ready. Safe to no-op if no AB forms exist.
     ------------------------------------------------------------------------ */
  function init() {
    var forms = document.querySelectorAll('form[data-ab-test="true"]');
    for (var i = 0; i < forms.length; i++) {
      applyCostaAB(forms[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
